import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MonitorIcon, LayoutIcon, CodeIcon, SmartphoneIcon, ArrowRightIcon, GiftIcon, TrendingUpIcon, MousePointer2Icon, SearchIcon, BarChart3Icon, ZapIcon, ClockIcon, UsersIcon, ClipboardCheckIcon, PenToolIcon, RocketIcon, HeadphonesIcon, ImagePlusIcon, ServerIcon, ShieldCheckIcon, RefreshCwIcon, HelpCircleIcon, ChevronDownIcon, LayoutTemplateIcon, CheckCircle2Icon } from 'lucide-react';
const plans = [{
  name: 'Starter Hosting',
  price: '$9.99',
  period: '/mo',
  desc: 'Perfect for small business websites and blogs.',
  features: ['1 Website', '50 GB SSD Storage', 'Unmetered Bandwidth', 'Free SSL Certificate', 'Free Domain for 1st Year']
}, {
  name: 'Business Pro',
  price: '$19.99',
  period: '/mo',
  desc: 'Ideal for growing businesses and e-commerce.',
  features: ['Unlimited Websites', 'Unmetered SSD Storage', 'Unmetered Bandwidth', 'Free SSL Certificate', 'Free Domain for 1st Year', 'Daily Backups'],
  popular: true
}];
const growthFeatures = [{
  icon: MousePointer2Icon,
  title: 'Lead Capture',
  desc: 'Strategically placed forms and CTAs to convert visitors into leads.'
}, {
  icon: SearchIcon,
  title: 'SEO Optimized',
  desc: 'Built-in search engine optimization to help your business get found.'
}, {
  icon: BarChart3Icon,
  title: 'Analytics',
  desc: 'Full tracking of visitor behavior and conversion metrics.'
}, {
  icon: ZapIcon,
  title: 'CRM Integration',
  desc: 'Sync leads directly with your existing sales and marketing tools.'
}];
const whyChooseUs = [{
  icon: MonitorIcon,
  title: 'Telecom Expertise',
  desc: 'We understand your industry and what your customers are looking for.'
}, {
  icon: ClockIcon,
  title: 'Fast Delivery',
  desc: 'Professional websites launched in weeks, not months.'
}, {
  icon: UsersIcon,
  title: 'Partner Focused',
  desc: 'We grow when you grow. Our success is tied to your business growth.'
}];
const processSteps = [{
  icon: ClipboardCheckIcon,
  title: 'Discovery',
  desc: 'We learn about your business goals and target audience.'
}, {
  icon: PenToolIcon,
  title: 'Design',
  desc: 'Custom layout design focused on your brand identity.'
}, {
  icon: RocketIcon,
  title: 'Launch',
  desc: 'Final testing and deployment to your custom domain.'
}, {
  icon: HeadphonesIcon,
  title: 'Support',
  desc: 'Ongoing maintenance and technical updates.'
}];
export function WebsiteDesignPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div className="bg-white" data-id="element-2195">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-900" data-id="element-2196">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-2197"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-2198"></div>
        <div className="absolute top-0 right-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-pink-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-2199"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2200">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-2201">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-2202">
              <GiftIcon className="w-4 h-4 mr-2 text-pink-400" data-id="element-2203" />
              Exclusive Offer
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-2204">
              Free Custom
              <br className="hidden md:block" data-id="element-2205" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-2206">
                Website Design
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-2207">
              Tell us what you need, and our expert team will design and build
              it for you. <strong data-id="element-2208">You only pay for the hosting.</strong> The
              entire design process is 100% free of charge.
            </p>
            <button onClick={() => onNavigate('contact')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-2209">
              Start Your Free Design
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2210" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Value Prop Section with Image */}
      <section className="py-24 bg-slate-50" data-id="element-2211">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2212">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2213">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-2214">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-2215">
                How does a "Free" website work?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-2216">
                It's simple: every website needs a place to live on the internet
                (hosting). Instead of charging you thousands of dollars for the
                design and development of your site, we waive those fees
                entirely.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-2217">
                <strong data-id="element-2218">You simply tell our team what you need.</strong> We
                handle the layout, the graphics, the mobile optimization, and
                the coding. You only pay a standard monthly fee for our premium,
                high-speed hosting.
              </p>

              <ul className="space-y-4" data-id="element-2219">
                {['Zero design or development fees', 'You provide the vision, we do the work', 'Premium, secure hosting included', 'Full ownership of your content'].map((item, i) => <li key={i} className="flex items-center text-slate-700 font-medium" data-id="element-2220">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 shrink-0" data-id="element-2221">
                      <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-2222"></div>
                    </div>
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-2223">
              <img src="https://images.unsplash.com/photo-1522542550221-31fd19575a2d?w=800&h=600&fit=crop" alt="Web design team working" className="w-full h-full object-cover" data-id="element-2224" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent flex items-end p-8" data-id="element-2225">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl text-white" data-id="element-2226">
                  <h3 className="font-bold text-xl mb-2" data-id="element-2227">Expert Design Team</h3>
                  <p className="text-slate-200 text-sm" data-id="element-2228">
                    Our in-house developers bring your vision to life at no
                    extra cost.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why are we giving away free websites? Section */}
      <section className="py-24 bg-slate-50" data-id="element-2229">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2230">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2231">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-2232">
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-8" data-id="element-2233">
                Why are we giving away free websites?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-2234">
                At Ambient Telecom, our goal is to help your business thrive. We
                know that a strong online presence is just as critical as
                reliable internet and phone systems.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-2235">
                By offering free, high-quality website design to our telecom
                customers, we help you generate more leads, close more sales,
                and grow your bottom line. When your business grows, our
                partnership grows.
              </p>

              <ul className="space-y-5" data-id="element-2236">
                {['Professional, modern layout design', 'Mobile-responsive layouts & fluid grids', 'SEO-friendly semantic HTML'].map((item, i) => <li key={i} className="flex items-center text-slate-700 font-semibold text-lg" data-id="element-2237">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-4 shrink-0" data-id="element-2238">
                      <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-2239" />
                    </div>
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2240">
              {[{
              icon: MonitorIcon,
              title: 'Custom Design',
              desc: 'Tailored completely to your corporate branding and visual style guidelines.'
            }, {
              icon: SmartphoneIcon,
              title: 'Mobile Ready',
              desc: 'Perfect fluid behaviors on iOS, Android, and tablets.'
            }, {
              icon: TrendingUpIcon,
              title: 'Lead Focused',
              desc: 'Optimized call-to-action flows and audit form placements.'
            }, {
              icon: CodeIcon,
              title: 'Fast & Secure',
              desc: 'Optimized files and assets for fast page speeds.'
            }].map((card, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group" data-id="element-2241">
                  <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary/10 transition-colors" data-id="element-2242">
                    <card.icon className="w-7 h-7 text-slate-900 group-hover:text-primary transition-colors" data-id="element-2243" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2244">
                    {card.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed" data-id="element-2245">
                    {card.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Growth Features */}
      <section className="py-24 bg-white" data-id="element-2246">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2247">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2248">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2249">
              Built for Growth
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2250">
              Our websites are more than just digital business cards—they are
              powerful sales tools.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2251">
            {growthFeatures.map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="p-8 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-xl transition-all" data-id="element-2252">
                <feature.icon className="w-10 h-10 text-primary mb-6" data-id="element-2253" />
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2254">
                  {feature.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed" data-id="element-2255">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="py-24 bg-slate-50" data-id="element-2256">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2257">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2258">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2259">
              Recent Transformations
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2260">
              See how a modern web presence drives real business results.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2261">
            {[{
            industry: 'Local Plumber',
            result: '+150% Online Leads',
            desc: 'Replaced an outdated 10-year-old site with a mobile-first design featuring click-to-call buttons and an emergency service form.',
            color: 'bg-blue-500'
          }, {
            industry: 'Law Firm',
            result: 'Top 3 Local Ranking',
            desc: 'Built an SEO-optimized site with dedicated practice area pages, resulting in a massive boost in local search visibility.',
            color: 'bg-slate-800'
          }, {
            industry: 'Medical Clinic',
            result: 'Streamlined Booking',
            desc: 'Integrated a secure patient portal and online appointment scheduling, reducing front-desk call volume by 40%.',
            color: 'bg-teal-500'
          }].map((project, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white rounded-3xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-shadow" data-id="element-2262">
                <div className={`h-40 ${project.color} flex items-center justify-center relative overflow-hidden`} data-id="element-2263">
                  <div className="absolute inset-0 bg-black/10" data-id="element-2264"></div>
                  <LayoutIcon className="w-16 h-16 text-white/50" data-id="element-2265" />
                </div>
                <div className="p-8" data-id="element-2266">
                  <div className="text-sm font-bold text-primary uppercase tracking-wider mb-2" data-id="element-2267">
                    {project.industry}
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-4" data-id="element-2268">
                    {project.result}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-2269">
                    {project.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-2270">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2271">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2272">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-2273">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2274">
                Why Partner With Us for Web Design?
              </h2>
              <p className="text-slate-400 text-lg mb-8" data-id="element-2275">
                Most web agencies don't understand the telecom landscape. We do.
                We build websites that speak directly to your customers' needs.
              </p>
              <div className="space-y-6" data-id="element-2276">
                {whyChooseUs.map((item, i) => <div key={i} className="flex gap-4" data-id="element-2277">
                    <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center shrink-0" data-id="element-2278">
                      <item.icon className="w-6 h-6 text-primary" data-id="element-2279" />
                    </div>
                    <div data-id="element-2280">
                      <h4 className="font-bold text-white" data-id="element-2281">{item.title}</h4>
                      <p className="text-sm text-slate-400" data-id="element-2282">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="bg-white/5 p-10 rounded-3xl border border-white/10" data-id="element-2283">
              <h3 className="text-2xl font-bold mb-6" data-id="element-2284">
                Lead Generation Machine
              </h3>
              <p className="text-slate-400 mb-8" data-id="element-2285">
                Our designs are focused on one thing: conversion. We use proven
                psychological triggers and UX patterns to guide visitors toward
                your contact forms.
              </p>
              <div className="p-6 bg-primary/10 rounded-2xl border border-primary/20" data-id="element-2286">
                <p className="text-primary font-bold text-lg mb-2" data-id="element-2287">
                  Average Results:
                </p>
                <div className="space-y-2" data-id="element-2288">
                  <div className="flex justify-between" data-id="element-2289">
                    <span className="text-slate-300" data-id="element-2290">Conversion Rate</span>
                    <span className="text-white font-bold" data-id="element-2291">+45%</span>
                  </div>
                  <div className="flex justify-between" data-id="element-2292">
                    <span className="text-slate-300" data-id="element-2293">Mobile Traffic</span>
                    <span className="text-white font-bold" data-id="element-2294">+60%</span>
                  </div>
                  <div className="flex justify-between" data-id="element-2295">
                    <span className="text-slate-300" data-id="element-2296">Page Load Speed</span>
                    <span className="text-white font-bold" data-id="element-2297">&lt; 2s</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process with Images */}
      <section className="py-24 bg-white" data-id="element-2298">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2299">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2300">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2301">
              Our Simple Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2302">
              We make getting a professional website completely stress-free.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-2303">
            {[{
            icon: ClipboardCheckIcon,
            title: '1. Tell Us What You Need',
            desc: 'We start with a quick consultation. You share your business goals, branding, and what you want the website to achieve.',
            image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop'
          }, {
            icon: PenToolIcon,
            title: '2. We Design It For Free',
            desc: 'Our expert team gets to work. We design, build, and optimize your entire website without charging you a dime for development.',
            image: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=600&h=400&fit=crop'
          }, {
            icon: ServerIcon,
            title: '3. Host & Launch',
            desc: 'Once you approve the design, you simply pay for our reliable, high-speed hosting, and we launch your site to the world.',
            image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'
          }].map((step, i) => <div key={i} className="flex flex-col bg-slate-50 rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-2304">
                <div className="h-48 overflow-hidden relative" data-id="element-2305">
                  <img src={step.image} alt={step.title} className="w-full h-full object-cover" data-id="element-2306" />
                  <div className="absolute top-4 left-4 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-2307">
                    <step.icon className="w-6 h-6 text-primary" data-id="element-2308" />
                  </div>
                </div>
                <div className="p-8 text-center flex-1 flex-col" data-id="element-2309">
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2310">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2311">
                    {step.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Hosting & Maintenance */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-2312">
        <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500/10 rounded-full blur-[100px]" data-id="element-2313"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2314">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2315">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2316">
              Beyond the Launch
            </h2>
            <p className="text-lg text-slate-400" data-id="element-2317">
              A website is never truly "finished." We provide ongoing hosting
              and maintenance to keep your site fast, secure, and up-to-date.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2318">
            {[{
            icon: ServerIcon,
            title: 'Cloud Hosting',
            desc: 'Lightning-fast, reliable hosting on premium cloud servers.'
          }, {
            icon: ShieldCheckIcon,
            title: 'SSL Security',
            desc: 'Free SSL certificates to keep visitor data encrypted and secure.'
          }, {
            icon: RefreshCwIcon,
            title: 'Daily Backups',
            desc: 'Automatic daily backups so you never lose your content.'
          }, {
            icon: CodeIcon,
            title: 'Core Updates',
            desc: 'We handle all technical updates to plugins and frameworks.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-id="element-2319">
                <item.icon className="w-10 h-10 text-pink-400 mx-auto mb-4" data-id="element-2320" />
                <h3 className="text-xl font-bold mb-3" data-id="element-2321">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-2322">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Domain Search Section */}
      <section className="py-20 bg-[#F8FAFC]" data-id="element-2323">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2324">
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#011B33] mb-4" data-id="element-2325">
            Find Your Digital Home
          </h2>
          <p className="text-lg text-slate-500 mb-10 max-w-2xl mx-auto" data-id="element-2326">
            Secure your domain name before someone else does.
          </p>

          <div className="relative max-w-3xl mx-auto" data-id="element-2327">
            <div className="flex items-center bg-white rounded-2xl p-2 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100" data-id="element-2328">
              <div className="pl-4 pr-2 text-slate-400" data-id="element-2329">
                <LayoutTemplateIcon className="w-6 h-6" data-id="element-2330" />
              </div>
              <input type="text" placeholder="search-your-dream-domain.com" className="flex-1 bg-transparent border-none focus:ring-0 text-lg text-slate-700 placeholder-slate-400 px-2 py-4 outline-none" data-id="element-2331" />
              <button className="bg-[#22A39F] hover:bg-[#1d8a87] text-white font-bold text-lg px-8 py-4 rounded-xl transition-colors" data-id="element-2332">
                Search
              </button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 mt-6 text-sm font-bold text-slate-400" data-id="element-2333">
              <span data-id="element-2334">.COM $12.99</span>
              <span data-id="element-2335">.NET $14.99</span>
              <span data-id="element-2336">.ORG $15.99</span>
              <span data-id="element-2337">.AI $59.99</span>
            </div>
          </div>
        </div>
      </section>

      {/* Hosting Packages Section */}
      <section className="py-24 bg-white" data-id="element-2338">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2339">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2340">
            <h2 className="text-3xl md:text-4xl font-bold text-[#011B33] mb-4" data-id="element-2341">
              Simple, Transparent Hosting
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2342">
              Your custom design is free. Just choose the hosting plan that fits
              your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-2343">
            {plans.map((plan, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className={`relative bg-white rounded-3xl p-8 md:p-10 border ${plan.popular ? 'border-[#22A39F] shadow-xl shadow-[#22A39F]/10' : 'border-slate-200 shadow-sm'}`} data-id="element-2344">
                {plan.popular && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#22A39F] text-white px-4 py-1 rounded-full text-sm font-bold tracking-wider uppercase" data-id="element-2345">
                    Most Popular
                  </div>}
                <h3 className="text-2xl font-bold text-[#011B33] mb-2" data-id="element-2346">
                  {plan.name}
                </h3>
                <p className="text-slate-500 mb-6" data-id="element-2347">{plan.desc}</p>
                <div className="flex items-baseline gap-1 mb-8" data-id="element-2348">
                  <span className="text-5xl font-extrabold text-[#011B33]" data-id="element-2349">
                    {plan.price}
                  </span>
                  <span className="text-slate-500 font-medium" data-id="element-2350">
                    {plan.period}
                  </span>
                </div>
                <ul className="space-y-4 mb-8" data-id="element-2351">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-center gap-3 text-slate-600" data-id="element-2352">
                      <CheckCircle2Icon className="w-5 h-5 text-[#22A39F] shrink-0" data-id="element-2353" />
                      <span data-id="element-2354">{feature}</span>
                    </li>)}
                </ul>
                <button onClick={() => onNavigate('contact')} className={`w-full py-4 rounded-xl font-bold text-lg transition-colors ${plan.popular ? 'bg-[#22A39F] hover:bg-[#1d8a87] text-white' : 'bg-slate-100 hover:bg-slate-200 text-[#011B33]'}`} data-id="element-2355">
                  Select Plan
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Web Design FAQ */}
      <section className="py-24 bg-slate-50" data-id="element-2356">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2357">
          <div className="text-center mb-16" data-id="element-2358">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-2359">
              <HelpCircleIcon className="w-8 h-8 text-pink-500" data-id="element-2360" />
              Web Design FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2361">
              Common questions about our free website program.
            </p>
          </div>
          <div className="space-y-4" data-id="element-2362">
            {[{
            q: 'Do I own the website?',
            a: 'Yes! Once the site is launched, you own all the content and design. If you ever decide to leave our hosting, you can take the website with you.'
          }, {
            q: 'How long does it take to build?',
            a: 'Most standard business websites are designed, developed, and launched within 3 to 4 weeks, provided we have all the necessary content and approvals from you.'
          }, {
            q: 'Will it work on mobile phones?',
            a: 'Absolutely. Over 60% of web traffic is mobile. Every site we build uses responsive design to ensure it looks perfect on phones, tablets, and desktops.'
          }, {
            q: 'Can I update the website myself?',
            a: "Yes. We build on user-friendly Content Management Systems (CMS). We'll provide training so you can easily update text, swap images, or add blog posts without knowing how to code."
          }, {
            q: 'What if I need structural changes later?',
            a: 'Our ongoing maintenance packages include minor monthly updates. For larger structural changes or new page additions, we offer discounted hourly rates for our telecom partners.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-2363">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-2364">
                  <span className="font-bold text-slate-900" data-id="element-2365">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-2366">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-2367" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-2368">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2369">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-2370">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-20 relative overflow-hidden" data-id="element-2371">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-2372"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-2373">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2374">
            Ready for your free website?
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-2375">
            Contact us today to discuss your vision. You only pay for hosting,
            we handle the rest.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-50 transition-all shadow-xl inline-flex items-center" data-id="element-2376">
            Let's Start Building
            <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2377" />
          </button>
        </div>
      </section>
    </div>;
}